// pages/alerts/alertsUi.jsx — SOUND-847 shared render pieces for /alerts.
//
// Rendering only. Every rule (thresholds, stale, ago) is in utilities/alertCoverage.
import React from "react";
import { AlertTriangle } from "lucide-react";
import RecordStatusPill from "components/Records/RecordStatusPill";
import { statusToneFrom } from "utilities/statusTones";
import { rateTone, formatRate, agoLabel, isStale, isLinked } from "utilities/alertCoverage";

// Tone key → the house badge/dot classes. The LABEL is replaced with the rate.
const RATE_TONE = statusToneFrom({
  destructive: { label: "Below 85%", tone: "destructive" },
  warning: { label: "85–90%", tone: "warning" },
  success: { label: "90% or above", tone: "success" },
  muted: { label: "No data", tone: "muted" },
});

/** Coverage / refund-rate pill: red < 85%, amber 85–90%, green ≥ 90%, muted when null. */
export function RatePill({ value }) {
  return (
    <RecordStatusPill
      status={value}
      toneFor={(v) => ({ ...RATE_TONE(rateTone(v)), label: formatRate(v) })}
    />
  );
}

/** PageKpis tone for a rate (it has no "muted"; neutral is its equivalent). */
export function kpiToneFor(v) {
  const t = rateTone(v);
  return t === "muted" ? "neutral" : t;
}

/**
 * Series colours. The same first four hues as the house analytics palette
 * (components/analytics/ReportChart PALETTE), so a program reads the same as a
 * series anywhere else in the app. Chargebacks without an alert use the
 * destructive token; matched ones the muted residual grey.
 */
export const PROGRAM_COLORS = {
  ethoca: "hsl(146 70% 45%)",
  cdrn: "hsl(217 91% 60%)",
  rdr: "hsl(280 65% 60%)",
  tc40: "hsl(31 92% 55%)",
};

export function Swatch({ color }) {
  return (
    <span
      className="mr-1.5 inline-block h-2 w-2 shrink-0 rounded-sm align-middle"
      style={{ background: color }}
      aria-hidden="true"
    />
  );
}

/** "CRM" when the MID is linked, "Disputifier" when it is not; nothing for merchant rows. */
export function LinkBadge({ row }) {
  const linked = isLinked(row);
  if (linked === true) return <span className="badge badge-brand ml-1.5 shrink-0">CRM</span>;
  if (linked === false) return <span className="badge badge-muted ml-1.5 shrink-0">Disputifier</span>;
  return null;
}

/** "Nd ago" with a ⚠ and warning colour when the date is 7+ days before asOf. */
export function AgoText({ date, asOf, fallback = "—" }) {
  const label = agoLabel(date, asOf);
  if (!label) return <span className="text-muted-foreground">{fallback}</span>;
  if (isStale(date, asOf)) {
    return (
      <span className="inline-flex items-center gap-1 font-medium text-warning" title={`Last received ${date}`}>
        <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
        {label}
      </span>
    );
  }
  return (
    <span className="text-muted-foreground" title={`Last received ${date}`}>
      {label}
    </span>
  );
}

/** Small uppercase section heading — the ticketing rail chrome. */
export function SectionHeading({ children }) {
  return (
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  );
}
